var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ROOT_MODEL_UID, ByteStreamWriter, wrapMessageWithoutMetaData, TYPE_HASH_BYTES_PER_ELEMENT } from "flavr-common";
import { WebRTC } from "../mediaStream/WebRTC";
import { BufferMessageType } from "../MultiplayerTypes";
import { FragmentFlag } from "./PeerMessageTypes";
import { MediaConnections } from "./MediaConnections";
export class PeerConnection {
    constructor(clientId, remoteClientId, remoteClientIdx, initiator) {
        this.remoteClientIdx = remoteClientIdx;
        this.peerStatistics = new Map();
        this.peerConnectionStats = {};
        this.prevUpdateTime = 0;
        this.remoteTime = 0;
        this.rtt = 0;
        this.latency = 0;
        this.currentRoomModelUID = ROOT_MODEL_UID;
        this.pendingUnreliableMessages = [];
        this.pendingReliableMessages = [];
        this.pendingReliableBusMessages = [];
        this.pendingUnreliableBusMessages = [];
        this.pendingReliableBufferMessages = [];
        this.pendingUnreliableBufferMessages = [];
        this.transportMap = new Map();
        this.pendingTransportRequests = new Map();
        const dataTransportId = "data";
        this.webRTC = new WebRTC(dataTransportId, clientId, remoteClientId, initiator);
        this.registerTransport(dataTransportId, this.webRTC);
        this.mediaConnections = new MediaConnections(this);
        this.prevUpdateTime = Date.now();
    }
    registerTransport(id, transport) {
        this.transportMap.set(id, transport);
        const pendings = this.pendingTransportRequests.get(id);
        if (pendings) {
            for (const data of pendings) {
                data.resolve(transport);
            }
            this.pendingTransportRequests.delete(id);
        }
    }
    waitForTransport(transportId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (transportId !== "send" && transportId !== "recv") {
                throw new Error(`waitForTransport wrong id ${transportId}`);
            }
            const transport = this.transportMap.get(transportId);
            if (transport) {
                return transport;
            }
            const promise = new Promise((resolve, reject) => {
                let pendings = this.pendingTransportRequests.get(transportId);
                if (!pendings) {
                    this.pendingTransportRequests.set(transportId, (pendings = []));
                }
                pendings.push({
                    resolve,
                    reject
                });
            });
            return yield promise;
        });
    }
    isInVisibleRoom(ownerRoomUID) {
        return ownerRoomUID === this.currentRoomModelUID;
    }
    close() {
        for (const pendings of this.pendingTransportRequests.values()) {
            for (const data of pendings) {
                data.reject();
            }
        }
        this.pendingTransportRequests.clear();
        this.transportMap.clear();
        this.webRTC.close();
        void this.mediaConnections.close();
    }
    onDestroy() {
        this.webRTC.onDestroy();
    }
    getLatency() {
        return this.latency;
    }
    onRoomChanged(roomModelUID) {
        this.currentRoomModelUID = roomModelUID;
    }
    getCurrentRoomModelUID() {
        return this.currentRoomModelUID;
    }
    update() {
        const now = Date.now();
        const frameTime = now - this.prevUpdateTime;
        this.prevUpdateTime = now;
        this.updateRemoteTime(frameTime);
    }
    updateRemoteTime(frameTime) {
        if (this.remoteTime > 0) {
            this.remoteTime += frameTime;
        }
    }
    handleTimeSync(timestamp) {
        const buffer = new ArrayBuffer(Uint8Array.BYTES_PER_ELEMENT + Float64Array.BYTES_PER_ELEMENT * 2);
        const stream = new ByteStreamWriter(buffer);
        stream.uint8(BufferMessageType.TimeSyncAnswer);
        stream.float64(timestamp);
        stream.float64(Date.now()); // remoteTimestamp
        this.sendUnreliableBuffer(buffer);
    }
    handleTimeSyncAnswer(timestamp, remoteTimestamp) {
        const rtt = Date.now() - timestamp;
        if (this.rtt === 0) {
            this.rtt = rtt;
        }
        else {
            this.rtt = this.rtt * 0.88 + rtt * 0.12;
        }
        this.latency = Math.round(this.rtt / 2);
        this.remoteTime = remoteTimestamp - this.latency;
    }
    sendReliable(msg) {
        if (!this.webRTC.isDataChannelReliableReady()) {
            this.pendingReliableMessages.push(msg);
            return;
        }
        this.webRTC.sendReliable(JSON.stringify(msg));
    }
    sendReliableBuffer(buffer) {
        if (!this.webRTC.isDataChannelReliableReady()) {
            this.pendingReliableBufferMessages.push(buffer);
            return;
        }
        this.webRTC.sendReliableBuffer(buffer);
    }
    sendReliableBusMessages(messages) {
        if (!this.webRTC.isDataChannelBusMessageReady()) {
            this.pendingReliableBusMessages.push(...messages);
            return;
        }
        const buffer = this.packBusMsgInArrayBuffer(messages);
        if (!buffer)
            return;
        if (!Array.isArray(buffer)) {
            this.webRTC.sendBusMessage(buffer);
        }
        else {
            for (const b of buffer) {
                this.webRTC.sendBusMessage(b);
            }
        }
    }
    sendReliableBusMessageCached(msg) {
        this.pendingReliableBusMessages.push(msg);
    }
    sendUnreliable(msg) {
        if (!this.webRTC.isDataChannelUnreliableReady()) {
            this.pendingUnreliableMessages.push(msg);
            return;
        }
        this.webRTC.sendUnreliable(JSON.stringify(msg));
    }
    sendUnreliableBuffer(buffer) {
        if (!this.webRTC.isDataChannelUnreliableReady()) {
            this.pendingUnreliableBufferMessages.push(buffer);
            return;
        }
        this.webRTC.sendUnreliableBuffer(buffer);
    }
    flushMessages() {
        if (this.webRTC.isDataChannelUnreliableReady()) {
            if (this.pendingUnreliableBusMessages.length) {
                const buffer = this.packBusMsgInArrayBuffer(this.pendingUnreliableBusMessages, this.pendingUnreliableMessages);
                if (buffer) {
                    if (!Array.isArray(buffer)) {
                        this.sendUnreliableBuffer(buffer);
                    }
                    else {
                        for (const b of buffer) {
                            this.sendUnreliableBuffer(b);
                        }
                    }
                }
                this.pendingUnreliableBusMessages = [];
            }
            if (this.pendingUnreliableMessages.length) {
                this.sendUnreliable({
                    channel: "bulk",
                    payload: this.pendingUnreliableMessages
                });
                this.pendingUnreliableMessages = [];
            }
            if (this.pendingUnreliableBufferMessages.length) {
                for (const buffer of this.pendingUnreliableBufferMessages) {
                    this.sendUnreliableBuffer(buffer);
                }
                this.pendingUnreliableBufferMessages = [];
            }
        }
        if (this.webRTC.isDataChannelBusMessageReady()) {
            if (this.pendingReliableBusMessages.length) {
                const buffer = this.packBusMsgInArrayBuffer(this.pendingReliableBusMessages, this.pendingReliableMessages);
                if (buffer) {
                    if (!Array.isArray(buffer)) {
                        this.webRTC.sendBusMessage(buffer);
                    }
                    else {
                        for (const b of buffer) {
                            this.webRTC.sendBusMessage(b);
                        }
                    }
                }
                this.pendingReliableBusMessages = [];
            }
        }
        if (this.webRTC.isDataChannelReliableReady()) {
            if (this.pendingReliableMessages.length) {
                this.sendReliable({
                    channel: "bulk",
                    payload: this.pendingReliableMessages
                });
                this.pendingReliableMessages = [];
            }
            if (this.pendingReliableBufferMessages.length) {
                for (const buffer of this.pendingReliableBufferMessages) {
                    this.sendReliableBuffer(buffer);
                }
                this.pendingReliableBufferMessages = [];
            }
        }
    }
    createFragmentedBusMessage(msg, msgByteLength, maxByteLength, chunks) {
        const msgBuffer = new ArrayBuffer(msgByteLength);
        const msgStream = new ByteStreamWriter(msgBuffer);
        if (!msg.serialization || msg.typeHash === undefined) {
            return 0;
        }
        msg.serialization(msgStream);
        let writtenByteLength = 0;
        let msgOffset = 0;
        let firstChunk = true;
        while (msgByteLength > 0) {
            let headerByteLength = Uint8Array.BYTES_PER_ELEMENT * 2; // msgType & fragmentFlag
            if (firstChunk) {
                headerByteLength += Uint32Array.BYTES_PER_ELEMENT + TYPE_HASH_BYTES_PER_ELEMENT; // msgCount // typeHash
            }
            const chunkByteLength = Math.min(msgByteLength + headerByteLength, maxByteLength);
            msgByteLength -= chunkByteLength - headerByteLength;
            const fragmentFlag = msgByteLength <= 0 ? FragmentFlag.EndFragment : FragmentFlag.MoreFragments;
            const chunkBuffer = new ArrayBuffer(chunkByteLength);
            const chunkStream = new ByteStreamWriter(chunkBuffer);
            // Header
            chunkStream.uint8(BufferMessageType.BusMsg);
            chunkStream.uint8(fragmentFlag);
            if (firstChunk) {
                firstChunk = false;
                // Header
                chunkStream.uint32(1); // msgCount
                // Message info
                chunkStream.typeHash(msg.typeHash);
            }
            const msgChunkByteLength = chunkByteLength - chunkStream.getUsedByteLength();
            const msgChunk = new Uint8Array(msgStream.getBufferChunk(msgOffset, msgChunkByteLength));
            msgOffset += msgChunkByteLength;
            new Uint8Array(chunkBuffer).set(msgChunk, chunkStream.getUsedByteLength());
            chunks.push(chunkBuffer);
            writtenByteLength += chunkStream.getUsedByteLength() + msgChunkByteLength;
        }
        return writtenByteLength;
    }
    createBusMsgBuffer(messages, msgCount, byteLength, start, end, fallbackMessages) {
        const buffer = new ArrayBuffer(byteLength);
        const stream = new ByteStreamWriter(buffer);
        stream.uint8(BufferMessageType.BusMsg);
        stream.uint8(FragmentFlag.DontFragment);
        stream.uint32(msgCount);
        for (let i = start; i < end; ++i) {
            const msg = messages[i];
            if (msg.serialization && msg.typeHash !== undefined) {
                stream.typeHash(msg.typeHash);
                msg.serialization(stream);
            }
            else {
                if (fallbackMessages === undefined) {
                    console.warn("[multiplayer] packMsgInArrayBuffer: Message doesn't send", msg.type);
                    continue;
                }
                const sendMessage = {
                    channel: "bus",
                    payload: {
                        msg: wrapMessageWithoutMetaData(msg)
                    }
                };
                fallbackMessages.push(sendMessage);
            }
        }
        return buffer;
    }
    packBusMsgInArrayBuffer(messages, fallbackMessages) {
        const headerByteLength = Uint8Array.BYTES_PER_ELEMENT + // msg type
            Uint8Array.BYTES_PER_ELEMENT + // fragmentFlag
            Uint32Array.BYTES_PER_ELEMENT; // msgCount
        let byteLength = headerByteLength;
        let msgCount = 0;
        let buffers;
        let lastIndex = 0;
        let lastByteLength = 0;
        let lastMsgCount = 0;
        const createBusMsgBufferFunc = (index) => {
            if (buffers === undefined) {
                buffers = [];
            }
            buffers.push(this.createBusMsgBuffer(messages, msgCount - lastMsgCount, byteLength - lastByteLength, lastIndex, index, fallbackMessages));
            lastIndex = index;
            lastByteLength = byteLength;
            lastMsgCount = msgCount;
            byteLength += headerByteLength;
        };
        for (let i = 0, len = messages.length; i < len; ++i) {
            const msg = messages[i];
            if (!msg.serialization)
                continue;
            if (msg.typeHash === undefined)
                continue;
            let msgByteLength = msg.byteLength;
            if (msgByteLength === undefined)
                continue;
            msgByteLength += TYPE_HASH_BYTES_PER_ELEMENT; // typeHash
            // Message fragmentation
            if (msgByteLength > this.webRTC.maxMessageSize) {
                if (buffers === undefined) {
                    buffers = [];
                }
                if (lastByteLength > 0) {
                    createBusMsgBufferFunc(i);
                }
                this.createFragmentedBusMessage(messages[i], msgByteLength, this.webRTC.maxMessageSize, buffers);
                lastIndex = i + 1;
                lastByteLength = byteLength + msgByteLength;
                lastMsgCount = msgCount + 1;
                byteLength += headerByteLength;
            }
            else if (byteLength + msgByteLength - lastByteLength > this.webRTC.maxMessageSize) {
                createBusMsgBufferFunc(i);
            }
            byteLength += msgByteLength;
            ++msgCount;
        }
        if (msgCount === 0) {
            if (fallbackMessages) {
                const newArray = messages.map((msg) => ({
                    channel: "bus",
                    payload: {
                        msg: wrapMessageWithoutMetaData(msg)
                    }
                }));
                fallbackMessages.push(...newArray);
            }
            return;
        }
        if (buffers === undefined) {
            return this.createBusMsgBuffer(messages, msgCount, byteLength, 0, messages.length, fallbackMessages);
        }
        else {
            const missingBytes = byteLength - lastByteLength;
            if (missingBytes > 0) {
                buffers.push(this.createBusMsgBuffer(messages, msgCount - lastMsgCount, byteLength - lastByteLength, lastIndex, messages.length, fallbackMessages));
            }
            return buffers;
        }
    }
}
